import { FunctionComponent } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import type { UpdateType } from '@app/app-updates/src/schema';

import { UpdateStatus } from '@app/app-updates-table/src/components/UpdateStatus';
import { UpdateChangeType } from '@app/app-updates-table/src/components/UpdateChangeType';
import { UpdateImpactRating } from '@app/app-updates-table/src/components/UpdateImpactRating';
import { UpdateTags } from '@app/app-updates-table/src/components/UpdateTags';
import { AIDisclaimer } from './AIDisclaimer';
import { fg, featureGateKeys } from '@app/feature-gates';
import { DateRangeInfo } from '@app/common';

const summaryItemStyles = xcss({
  minWidth: '0',
  alignItems: 'flex-start',
});

// Ensures all header labels align on the same baseline,
// even when some headers include the AIDisclaimer (icon + lozenge).
const headerStyles = xcss({
  minHeight: '24px',
  display: 'flex',
  alignItems: 'center',
});

const tagsStyles = xcss({
  marginInlineStart: 'space.negative.050',
});

const messages = defineMessages({
  rolloutSchedule: {
    id: 'appUpdatesDetails.changelog.rolloutSchedule',
    defaultMessage: 'Rollout schedule',
    description: 'Label for rollout schedule information',
  },
  status: {
    id: 'appUpdatesDetails.changelog.status',
    defaultMessage: 'Status',
    description: 'Label for update status information',
  },
  changeType: {
    id: 'appUpdatesDetails.changelog.changeType',
    defaultMessage: 'Change type',
    description: 'Label for original (non-AI) change type',
  },
  changeImpact: {
    id: 'appUpdatesDetails.changelog.changeImpact',
    defaultMessage: 'Change impact',
    description: 'Label for AI-generated impact rating',
  },
  contextualTags: {
    id: 'appUpdatesDetails.changelog.contextualTags',
    defaultMessage: 'Contextual tags',
    description: 'Label for AI-generated contextual tags',
  },
});

export interface AppUpdatesChangelogSummaryProps {
  /**
   * The status of the update (e.g., COMING_SOON, PLANNED, ROLLING_OUT, etc.)
   */
  status?: UpdateType['status'] | null;

  /**
   * The type of change (e.g., IMPROVEMENT, FIX, ANNOUNCEMENT)
   */
  type?: UpdateType['type'] | null;

  /**
   * ISO date string for when the release starts
   */
  releaseStartDate?: string | null;

  /**
   * ISO date string for when the release ends
   */
  releaseEndDate?: string | null;

  /**
   * AI-generated impact rating for the update
   */
  impactRating?: UpdateType['impactRating'];

  /**
   * AI-generated contextual tags for the update
   */
  tags?: UpdateType['tags'];
}

/**
 * Displays a summary of changelog metadata including status, change type,
 * and release date information.
 *
 * Example:
 * ```tsx
 * <AppUpdatesChangelogSummary
 *   status="APPROVED"
 *   type="IMPROVEMENT"
 *   releaseStartDate="2024-03-01"
 *   releaseEndDate="2024-03-31"
 * />
 * ```
 */
export const AppUpdatesChangelogSummary: FunctionComponent<
  AppUpdatesChangelogSummaryProps
> = ({
  status,
  type,
  releaseStartDate,
  releaseEndDate,
  impactRating,
  tags,
}) => {
  const { formatMessage } = useIntl();
  const shouldShowContextualTags = fg(featureGateKeys.contextualTagsBeta);
  const hasBaseMetadata = Boolean(
    status || type || releaseStartDate || releaseEndDate,
  );
  const hasImpactRating = Boolean(impactRating);
  const hasContextualTags = Boolean(tags?.length);
  const shouldRenderImpactMetadata = hasBaseMetadata || hasImpactRating;
  const shouldRenderContextualTagsMetadata =
    shouldShowContextualTags && (hasBaseMetadata || hasContextualTags);

  // Don't render if no visible metadata is available.
  if (
    !hasBaseMetadata &&
    !hasImpactRating &&
    !(shouldShowContextualTags && hasContextualTags)
  ) {
    return null;
  }

  return (
    <Inline space="space.400" alignBlock="start" shouldWrap>
      {status && (
        <Stack space="space.100" xcss={summaryItemStyles}>
          <Box xcss={headerStyles}>
            <Text size="small" weight="bold" testId="changelog-status-label">
              {formatMessage(messages.status)}
            </Text>
          </Box>
          <UpdateStatus status={status} />
        </Stack>
      )}
      {type && (
        <Stack space="space.100" xcss={summaryItemStyles}>
          <Box xcss={headerStyles}>
            <Text size="small" weight="bold">
              {formatMessage(messages.changeType)}
            </Text>
          </Box>
          <UpdateChangeType type={type} />
        </Stack>
      )}
      {(releaseStartDate || releaseEndDate) && (
        <Stack space="space.100" xcss={summaryItemStyles}>
          <Box xcss={headerStyles}>
            <Text size="small" weight="bold">
              {formatMessage(messages.rolloutSchedule)}
            </Text>
          </Box>
          <DateRangeInfo
            startDate={releaseStartDate}
            endDate={releaseEndDate}
            status={status}
          />
        </Stack>
      )}
      {shouldRenderImpactMetadata && (
        <Stack space="space.100" xcss={summaryItemStyles} alignInline="start">
          <Box xcss={headerStyles}>
            <AIDisclaimer
              label={
                <Text size="small" weight="bold">
                  {formatMessage(messages.changeImpact)}
                </Text>
              }
            />
          </Box>
          {impactRating && <UpdateImpactRating impactRating={impactRating} />}
        </Stack>
      )}
      {shouldRenderContextualTagsMetadata && (
        <Stack space="space.0" xcss={summaryItemStyles}>
          <Box xcss={headerStyles}>
            <AIDisclaimer
              label={
                <Text size="small" weight="bold">
                  {formatMessage(messages.contextualTags)}
                </Text>
              }
            />
          </Box>
          {tags && tags.length > 0 && (
            <Box xcss={tagsStyles}>
              <UpdateTags tags={tags} />
            </Box>
          )}
        </Stack>
      )}
    </Inline>
  );
};

export default AppUpdatesChangelogSummary;
